import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { API_ENDPOINT } from "../../utils/constants";

export default function StateSelector({
  countryCode,
  onChange,
  required,
  selectedState,
}) {
  const abortControllerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);

  useEffect(() => {
    const fetchStates = async () => {
      if (!countryCode) {
        setStates([]);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_ENDPOINT}/visitor/get-states-of-country/${countryCode}`,
          { signal: controller.signal }
        );
        const data = await response.json();

        // Map and sort alphabetically by name
        const stateData = data
          .map((state) => ({
            code: state.isoCode,
            name: state.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Only update if still relevant
        if (!controller.signal.aborted) {
          setStates(stateData);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching states:", error);
          setStates([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchStates();

    return () => {
      // Cleanup pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [countryCode]);

  const handleChange = (event, value) => {
    onChange?.(value);
  };

  return (
    <Autocomplete
      disabled={!countryCode || loading}
      getOptionLabel={(option) => (option ? option.name : "")}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      loading={loading}
      noOptionsText={
        countryCode ? "No states available" : "Select country first"
      }
      onChange={handleChange}
      options={states}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          label="Select State"
          required={required}
          variant="outlined"
        />
      )}
      size="small"
      value={selectedState || null}
    />
  );
}
